/**
 * HRTemplatesManager — Phase 4 (2026-02).
 *
 * Full-page admin surface for HR to create/edit scorecard TEMPLATES
 * (per role + stage) and email TEMPLATES. Rendered inside a modal
 * launched from HR Settings.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api, { errMsg } from '../../lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Card } from '../ui/card';
import { Plus, Trash2, ClipboardList, Mail, ChevronDown, ChevronRight, Pencil } from 'lucide-react';
import { toast } from 'sonner';

const C = {
  ink: '#2D2833', ink2: '#685B72', soft: '#8A7D95',
  border: '#E8E5EC', paper: '#FBF8F4',
  plum: '#4A3B5F', gold: '#B08A3A', rose: '#8A3A3A',
};

const ROLES = ['SLP', 'CF-SLP', 'SLPA', 'PRN', 'Student-Extern',
                'Office Director/SLP', 'Admin'];
const STAGES = [
  { key: 'phone_screen', label: 'Phone screen' },
  { key: 'interview',    label: 'Interview' },
];
const KINDS = [
  { key: 'rating', label: 'Rating (1-N)' },
  { key: 'yn',     label: 'Yes / No' },
  { key: 'text',   label: 'Free text' },
];
const EMAIL_CATEGORIES = ['Phone screen invite', 'Interview invite', 'Offer',
                           'Decline', 'Reference request', 'Custom'];

// ─── Scorecard section ────────────────────────────────────────
function ScorecardManager() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [draft, setDraft] = useState({
    role: 'SLP', stage_context: 'phone_screen', name: '',
    questions: [{ prompt: 'Overall communication', kind: 'rating', max_score: 5, weight: 1 }],
  });

  const load = useCallback(() => {
    setLoading(true);
    api.get('/hr/scorecard-templates')
      .then(r => setList(r.data.templates || []))
      .catch(e => toast.error(errMsg(e, 'Could not load templates')))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const addQ = (setter) => setter(d => ({ ...d, questions: [...d.questions, { prompt: '', kind: 'rating', max_score: 5, weight: 1 }] }));
  const removeQ = (setter, i) => setter(d => ({ ...d, questions: d.questions.filter((_, idx) => idx !== i) }));
  const patchQ = (setter, i, patch) => setter(d => ({ ...d, questions: d.questions.map((q, idx) => idx === i ? { ...q, ...patch } : q) }));

  const create = async () => {
    if (!draft.name.trim()) return toast.error('Name required');
    if (!draft.questions.length) return toast.error('At least one question');
    try {
      await api.post('/hr/scorecard-templates', draft);
      toast.success('Scorecard template created');
      setDraft({ role: 'SLP', stage_context: 'phone_screen', name: '',
                 questions: [{ prompt: '', kind: 'rating', max_score: 5, weight: 1 }] });
      load();
    } catch (e) {
      toast.error(errMsg(e, 'Create failed'));
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this template?')) return;
    try { await api.delete(`/hr/scorecard-templates/${id}`); load(); } catch (e) { toast.error(errMsg(e, 'Delete failed')); }
  };

  return (
    <div className="space-y-3">
      {/* Existing */}
      <div>
        <div className="text-[11px] uppercase tracking-widest font-semibold mb-1" style={{ color: C.soft }}>Existing scorecards</div>
        {loading ? <div className="text-sm" style={{ color: C.soft }}>Loading…</div> :
          list.length === 0 ? <div className="text-sm" style={{ color: C.soft }}>No templates yet — create one below.</div> :
          <div className="space-y-1">
            {list.map(t => (
              <div key={t.id} className="text-[12px] rounded"
                   style={{ background: C.paper, border: `1px solid ${C.border}` }}
                   data-testid={`sc-tpl-${t.id}`}>
                <button onClick={() => setExpandedId(expandedId === t.id ? null : t.id)}
                        className="w-full flex items-center gap-2 px-2 py-1.5">
                  {expandedId === t.id ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                  <span style={{ color: C.ink }} className="flex-1 text-left">{t.name}</span>
                  <span className="text-[10px]" style={{ color: C.soft }}>{t.role} · {t.stage_context}</span>
                  <span className="text-[10px]" style={{ color: C.soft }}>{t.questions.length} q</span>
                  <Trash2 size={12} style={{ color: C.rose }}
                          onClick={e => { e.stopPropagation(); remove(t.id); }}
                          data-testid={`sc-tpl-delete-${t.id}`} />
                </button>
                {expandedId === t.id && (
                  <div className="border-t px-3 py-2 text-[11px]" style={{ borderColor: C.border, color: C.ink2 }}>
                    {t.questions.map(q => (
                      <div key={q.id} className="py-0.5">
                        <span style={{ color: C.ink }}>•</span> {q.prompt}
                        <span className="ml-2 text-[10px]" style={{ color: C.soft }}>({q.kind}{q.kind === 'rating' ? `, /${q.max_score}` : ''}, w={q.weight})</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        }
      </div>

      {/* Create new */}
      <Card className="p-3 border" style={{ borderColor: C.border }} data-testid="sc-new-card">
        <div className="text-[11px] uppercase tracking-widest font-semibold mb-2" style={{ color: C.gold }}>New scorecard</div>
        <div className="grid grid-cols-3 gap-2 mb-2">
          <div>
            <Label className="text-[11px]">Name</Label>
            <Input value={draft.name} onChange={e => setDraft(d => ({ ...d, name: e.target.value }))}
                   placeholder="SLP Phone Screen v1" className="text-[12px]" data-testid="sc-new-name" />
          </div>
          <div>
            <Label className="text-[11px]">Role</Label>
            <Select value={draft.role} onValueChange={v => setDraft(d => ({ ...d, role: v }))}>
              <SelectTrigger data-testid="sc-new-role"><SelectValue /></SelectTrigger>
              <SelectContent>{ROLES.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[11px]">Stage</Label>
            <Select value={draft.stage_context} onValueChange={v => setDraft(d => ({ ...d, stage_context: v }))}>
              <SelectTrigger data-testid="sc-new-stage"><SelectValue /></SelectTrigger>
              <SelectContent>{STAGES.map(s => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>

        {draft.questions.map((q, i) => (
          <div key={i} className="grid grid-cols-12 gap-2 items-end mb-2">
            <div className="col-span-6">
              <Label className="text-[10px]">Q{i + 1}</Label>
              <Input value={q.prompt} onChange={e => patchQ(setDraft, i, { prompt: e.target.value })}
                     placeholder="Communicates clearly on the phone" className="text-[12px]"
                     data-testid={`sc-new-q-prompt-${i}`} />
            </div>
            <div className="col-span-2">
              <Label className="text-[10px]">Kind</Label>
              <Select value={q.kind} onValueChange={v => patchQ(setDraft, i, { kind: v })}>
                <SelectTrigger data-testid={`sc-new-q-kind-${i}`}><SelectValue /></SelectTrigger>
                <SelectContent>{KINDS.map(k => <SelectItem key={k.key} value={k.key}>{k.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {q.kind === 'rating' && (
              <div className="col-span-1">
                <Label className="text-[10px]">Max</Label>
                <Input type="number" min="1" max="10" value={q.max_score || 5}
                       onChange={e => patchQ(setDraft, i, { max_score: Number(e.target.value) })}
                       className="text-[12px]" data-testid={`sc-new-q-max-${i}`} />
              </div>
            )}
            <div className="col-span-2">
              <Label className="text-[10px]">Weight</Label>
              <Input type="number" step="0.1" min="0.1" max="5" value={q.weight || 1}
                     onChange={e => patchQ(setDraft, i, { weight: Number(e.target.value) })}
                     className="text-[12px]" data-testid={`sc-new-q-weight-${i}`} />
            </div>
            <div className="col-span-1">
              <Button variant="outline" size="sm" onClick={() => removeQ(setDraft, i)}
                      className="h-8 w-8 p-0" data-testid={`sc-new-q-remove-${i}`}>
                <Trash2 size={12} />
              </Button>
            </div>
          </div>
        ))}

        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => addQ(setDraft)} data-testid="sc-new-q-add" className="gap-1">
            <Plus size={12} /> Add question
          </Button>
          <Button size="sm" onClick={create} data-testid="sc-new-submit"
                  style={{ background: C.plum, color: 'white' }} className="gap-1">
            <ClipboardList size={12} /> Create template
          </Button>
        </div>
      </Card>
    </div>
  );
}

// ─── Email templates section ──────────────────────────────────
function EmailTemplateManager() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [draft, setDraft] = useState({
    name: '', category: 'Phone screen invite',
    subject: 'Next step: quick chat about {{candidate.role}} at Thrive',
    body_markdown: `Hi {{candidate.first_name}},\n\nThanks for applying to the {{candidate.role}} opening at Thrive. Are you free for a 15-minute phone screen on {{date}} at {{time}}?\n\nLooking forward to it,\n{{sender.name}}`,
  });

  const load = useCallback(() => {
    setLoading(true);
    api.get('/hr/email-templates')
      .then(r => setList(r.data.templates || []))
      .catch(e => toast.error(errMsg(e, 'Could not load email templates')))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!draft.name.trim() || !draft.subject.trim() || !draft.body_markdown.trim()) {
      return toast.error('Name, subject and body required');
    }
    try {
      await api.post('/hr/email-templates', draft);
      toast.success('Email template created');
      setDraft(d => ({ ...d, name: '' }));
      load();
    } catch (e) {
      toast.error(errMsg(e, 'Create failed'));
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this template?')) return;
    try { await api.delete(`/hr/email-templates/${id}`); load(); } catch (e) { toast.error(errMsg(e, 'Delete failed')); }
  };

  return (
    <div className="space-y-3">
      <div>
        <div className="text-[11px] uppercase tracking-widest font-semibold mb-1" style={{ color: C.soft }}>Existing email templates</div>
        {loading ? <div className="text-sm" style={{ color: C.soft }}>Loading…</div> :
          list.length === 0 ? <div className="text-sm" style={{ color: C.soft }}>No templates yet — create one below.</div> :
          <div className="space-y-1">
            {list.map(t => (
              <div key={t.id} className="text-[12px] rounded"
                   style={{ background: C.paper, border: `1px solid ${C.border}` }}
                   data-testid={`em-tpl-${t.id}`}>
                <button onClick={() => setExpandedId(expandedId === t.id ? null : t.id)}
                        className="w-full flex items-center gap-2 px-2 py-1.5">
                  {expandedId === t.id ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                  <span style={{ color: C.ink }} className="flex-1 text-left">{t.name}</span>
                  <span className="text-[10px]" style={{ color: C.soft }}>{t.category}</span>
                  <Trash2 size={12} style={{ color: C.rose }}
                          onClick={e => { e.stopPropagation(); remove(t.id); }}
                          data-testid={`em-tpl-delete-${t.id}`} />
                </button>
                {expandedId === t.id && (
                  <div className="border-t px-3 py-2 text-[11px]" style={{ borderColor: C.border }}>
                    <div><span style={{ color: C.soft }}>Subject:</span> {t.subject}</div>
                    <div className="whitespace-pre-wrap mt-1" style={{ color: C.ink2 }}>{t.body_markdown}</div>
                  </div>
                )}
              </div>
            ))}
          </div>
        }
      </div>

      <Card className="p-3 border" style={{ borderColor: C.border }} data-testid="em-new-card">
        <div className="text-[11px] uppercase tracking-widest font-semibold mb-2" style={{ color: C.gold }}>New email template</div>
        <div className="grid grid-cols-2 gap-2 mb-2">
          <div>
            <Label className="text-[11px]">Name</Label>
            <Input value={draft.name} onChange={e => setDraft(d => ({ ...d, name: e.target.value }))}
                   placeholder="SLP phone screen invite" className="text-[12px]" data-testid="em-new-name" />
          </div>
          <div>
            <Label className="text-[11px]">Category</Label>
            <Select value={draft.category} onValueChange={v => setDraft(d => ({ ...d, category: v }))}>
              <SelectTrigger data-testid="em-new-cat"><SelectValue /></SelectTrigger>
              <SelectContent>{EMAIL_CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        <div className="mb-2">
          <Label className="text-[11px]">Subject</Label>
          <Input value={draft.subject} onChange={e => setDraft(d => ({ ...d, subject: e.target.value }))}
                 className="text-[12px]" data-testid="em-new-subject" />
        </div>
        <div className="mb-2">
          <Label className="text-[11px]">Body (Markdown supported: **bold**, *italic*, paragraphs)</Label>
          <Textarea rows={6} value={draft.body_markdown}
                    onChange={e => setDraft(d => ({ ...d, body_markdown: e.target.value }))}
                    className="text-[12px] font-mono" data-testid="em-new-body" />
        </div>
        <div className="text-[10.5px]" style={{ color: C.soft }}>
          Available tokens: <code>{'{{candidate.name}}'}</code>, <code>{'{{candidate.first_name}}'}</code>, <code>{'{{candidate.role}}'}</code>, <code>{'{{clinic}}'}</code>, <code>{'{{date}}'}</code>, <code>{'{{time}}'}</code>, <code>{'{{sender.name}}'}</code>
        </div>
        <div className="mt-2">
          <Button size="sm" onClick={create} data-testid="em-new-submit"
                  style={{ background: C.plum, color: 'white' }} className="gap-1">
            <Mail size={12} /> Create template
          </Button>
        </div>
      </Card>
    </div>
  );
}

// ─── Root dialog ─────────────────────────────────────────────
export default function HRTemplatesManager({ open, onOpenChange }) {
  const [tab, setTab] = useState('scorecard');
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="hr-templates-manager" className="max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle style={{ color: C.ink }}>HR templates</DialogTitle>
          <DialogDescription className="text-[12px]" style={{ color: C.soft }}>
            Build reusable scorecards (per role + stage) and email templates HR can pick from inside every candidate.
          </DialogDescription>
        </DialogHeader>

        <div className="border-b flex gap-1" style={{ borderColor: C.border }}>
          {[
            { key: 'scorecard', label: 'Scorecards', icon: ClipboardList },
            { key: 'email',     label: 'Emails',     icon: Mail },
          ].map(t => {
            const Icon = t.icon;
            const active = t.key === tab;
            return (
              <button key={t.key} onClick={() => setTab(t.key)}
                      data-testid={`tpl-tab-${t.key}`}
                      className="px-3 py-2 flex items-center gap-2 text-sm border-b-2 transition-colors"
                      style={{
                        borderColor: active ? C.gold : 'transparent',
                        color: active ? C.ink : C.ink2,
                        fontWeight: active ? 600 : 400,
                      }}>
                <Icon size={13} strokeWidth={1.75} /> {t.label}
              </button>
            );
          })}
        </div>

        <div className="pt-3">
          {tab === 'scorecard' && <ScorecardManager />}
          {tab === 'email' && <EmailTemplateManager />}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="tpl-close">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
